import React from 'react';
import { useSelector } from 'react-redux';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import type { IJoinRequest } from '@wix/social-groups-serverless/dist/groups/types';
import { selectJoinRequestStatus } from 'store/selectors';

import { Button } from 'wui/Button';
import { InputDialog } from 'wui/InputDialog';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogContent } from 'wui/DialogContent';
import { DialogActions } from 'wui/DialogActions';
import { Stack } from 'wui/Stack';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { GroupQuestion } from 'common/components/GroupMembership/dialogs/GroupQuestionsDialog/GroupQuestion';

interface IReviewDialogProps {
  isOpen: boolean;
  groupId: string;
  request: IJoinRequest;

  onReject(): void;
  onClose(): void;
}

export function ReviewDialog(props: IReviewDialogProps) {
  const { groupId, request, isOpen, onClose } = props;

  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { members$ } = useController();

  const status = useSelector(
    selectJoinRequestStatus(request.profile.siteMemberId as string),
  );

  useDidUpdate(() => {
    if (!status.approve.loading && !status.approve.error) {
      handleClose();
    }
  }, [status.approve.loading, status.approve.error]);

  return (
    <InputDialog
      isOpen={isOpen}
      onClose={handleClose}
      aria-labelledby="rw-dialog-title"
    >
      <DialogTitle
        id="rw-dialog-title"
        title={t('groups-web.membership-questions.answers-modal.title', {
          userName: request.profile.name,
        })}
      />
      <DialogContent divider>
        <Stack gap="SP3" direction="vertical">
          {request.answers?.map((answer, index) => (
            <GroupQuestion
              disabled
              index={index}
              key={answer.id}
              value={answer.answer}
              question={answer.question}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          outlined
          variant="basic"
          fullWidth={isMobile}
          onClick={handleReject}
        >
          {t('groups-web.members.pending.decline')}
        </Button>
        <Button
          variant="basic"
          fullWidth={isMobile}
          onClick={handleSubmit}
          disabled={status.approve.loading}
          loading={status.approve.loading}
        >
          {t('groups-web.members.pending.approve')}
        </Button>
      </DialogActions>
    </InputDialog>
  );

  function handleClose() {
    onClose();
  }

  function handleReject() {
    onClose();
    props.onReject();
  }

  function handleSubmit() {
    members$.approve(groupId, request.profile.siteMemberId as string);
  }
}

ReviewDialog.displayName = 'ReviewDialog';
